import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import videoService from '../services/videoService';
import { SERVER_URL } from '../config/baseUrl';

const VideoList = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVideos, setTotalVideos] = useState(0);
  const [limit] = useState(10); // Show 10 videos per page

  useEffect(() => {
    fetchVideos();
  }, [currentPage]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await videoService.getAdminVideos(currentPage, limit);
      setVideos(response.videos);
      setTotalPages(response.totalPages);
      setTotalVideos(response.total);
    } catch (err) {
      setError(err.message || 'Failed to fetch videos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      await videoService.deleteVideo(id);
      // Refresh the current page to get updated data
      fetchVideos();
    } catch (err) {
      alert(err.message || 'Failed to delete video');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await videoService.updateVideo(id, { isActive: !currentStatus });
      setVideos(videos.map(video => 
        video._id === id ? { ...video, isActive: !currentStatus } : video
      ));
    } catch (err) {
      alert(err.message || 'Failed to update video status');
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="container">
          <Navbar />
          <div className="loading">Loading videos...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Sidebar />
      <div className="dashboard">
        <Navbar />
        <div className="container">
        
        <div className="dashboard-header">
          <h1>All Videos</h1>
          <p>Manage your uploaded videos ({totalVideos} total)</p>
          
          <div className="dashboard-actions">
            <button 
              className="btn btn-primary" 
              onClick={() => navigate('/upload')}
            >
              Upload New Video
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {videos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'white' }}>
            <h2>No videos found</h2>
            <p>Upload your first video to get started!</p>
          </div>
        ) : (
          <div className="video-grid">
            {videos.map((video) => {
              // Only show thumbnail if user uploaded one
              const hasThumbnail = video.thumbnail && video.thumbnail.trim() !== '';
              const thumbnailUrl = hasThumbnail
                ? (video.thumbnail.startsWith('http') 
                    ? video.thumbnail 
                    : `${SERVER_URL}${video.thumbnail}`)
                : null;
              
              return (
              <div key={video._id} className="video-card">
                {hasThumbnail ? (
                  <img 
                    src={thumbnailUrl}
                    alt={video.title}
                    className="video-thumbnail"
                    onError={(e) => {
                      // If image fails to load, hide it
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div 
                    className="video-thumbnail" 
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '48px',
                      fontWeight: 'bold'
                    }}
                  >
                    {video.videoType === 'shorts' ? '🎬' : '📹'}
                  </div>
                )}
                <div className="video-content">
                  <h3>{video.title}</h3>
                  <p>{video.description}</p>
                  <p style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>
                    Type: <strong style={{ color: '#667eea' }}>
                      {video.videoType === 'shorts' ? '🎬 Shorts' : '📹 Video'}
                    </strong>
                  </p>
                  <p style={{ fontSize: '12px', color: '#999', marginBottom: '10px' }}>
                    Status: <strong style={{ color: video.isActive ? '#4caf50' : '#ff4757' }}>
                      {video.isActive ? 'Active' : 'Inactive'}
                    </strong>
                  </p>
                  <div className="video-actions">
                    <a 
                      href={video.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      Watch
                    </a>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => navigate(`/videos/edit/${video._id}`)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => handleToggleStatus(video._id, video.isActive)}
                    >
                      {video.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => handleDelete(video._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '10px', 
            marginTop: '30px',
            marginBottom: '30px'
          }}>
            <button 
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              Previous
            </button>
            
            <div style={{ 
              color: 'white', 
              display: 'flex', 
              gap: '5px',
              alignItems: 'center'
            }}>
              {/* First page */}
              {currentPage > 3 && (
                <>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setCurrentPage(1)}
                    style={{ minWidth: '40px' }}
                  >
                    1
                  </button>
                  {currentPage > 4 && <span>...</span>}
                </>
              )}
              
              {/* Pages around current */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  return page === currentPage || 
                         page === currentPage - 1 || 
                         page === currentPage + 1 ||
                         page === currentPage - 2 ||
                         page === currentPage + 2;
                })
                .map(page => (
                  <button
                    key={page}
                    className={`btn ${page === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setCurrentPage(page)}
                    style={{ minWidth: '40px' }}
                  >
                    {page}
                  </button>
                ))}
              
              {/* Last page */}
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && <span>...</span>}
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setCurrentPage(totalPages)}
                    style={{ minWidth: '40px' }}
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>
            
            <button 
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next
            </button>
            
            <span style={{ color: 'white', marginLeft: '10px' }}>
              Page {currentPage} of {totalPages}
            </span>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default VideoList;
